import { useState, useEffect, useContext } from "react";
import { useRecoilState } from "recoil";
import { currentSlideAtom } from "../../atom";
import { ViewerContext } from "../../page/Viewer";

import styled from "styled-components";
import { Modal, Input, message } from "antd";
import { DeleteOutlined, PlusCircleOutlined, EditOutlined, CloseOutlined } from "@ant-design/icons";

import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import { getSlideData, postComment, putComment, deleteComment, deleteAnnotation } from "../../api/slideAPI";
import { getStoredTempViewerName, ensureTempViewerIdentity } from "../../lib/tempViewer";
import { hasTempViewerSession } from "../../api/api";

interface DemoSideMenuProps {
  activePanel: string | null;
  onClose: () => void;
  isConsultationMode?: boolean;
  onRefreshAnnotations?: () => Promise<void>; // 어노테이션 수정/삭제 후 뷰어 overlay 갱신
}

type PendingAnnoAction =
  | { type: "delete"; annotation: any }
  | { type: "deleteComment"; comment: any };

const DemoSideMenu = ({ activePanel, onClose, isConsultationMode, onRefreshAnnotations }: DemoSideMenuProps) => {
  const { userCustomSet } = useContext(ViewerContext);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);

  const annotationInfo = useAnnotationInfo({
    currentSlide,
    setCurrentSlide,
    requirePasswordOnEdit: isConsultationMode,
    onAnnotationsRefresh: async (_annotations: any[]) => {
      // 뷰어 overlay 갱신 (DemoViewer의 handleGetAnnotations 호출)
      await onRefreshAnnotations?.();
    },
  });

  const [showCommentModal, setShowCommentModal] = useState<boolean>(false);
  const [commentContent, setCommentContent] = useState<string>("");
  const [commentEditPassword, setCommentEditPassword] = useState<string>("");
  const [currentCommentId, setCurrentCommentId] = useState<number>(0);

  // 어노테이션/코멘트 수정/삭제 비밀번호 모달
  const [annoPasswordModalOpen, setAnnoPasswordModalOpen] = useState(false);
  const [annoPasswordInput, setAnnoPasswordInput] = useState("");
  const [pendingAnnoAction, setPendingAnnoAction] = useState<PendingAnnoAction | null>(null);

  const storedName = getStoredTempViewerName();
  const isTempViewer = hasTempViewerSession();

  const refreshSlide = async () => {
    if (onRefreshAnnotations) {
      await onRefreshAnnotations();
    } else {
      const data = await getSlideData(currentSlide.slideId);
      if (!userCustomSet?.useAnnotation) data.annotations = [];
      setCurrentSlide(data);
    }
  };

  const addComment = async () => {
    if (commentContent !== "") {
      if (currentCommentId === 0) {
        // 신규 작성: consultation + tempViewer면 identity 확보 (세션 있으면 재사용, 없으면 최초 1회 입력)
        if (isConsultationMode && isTempViewer) {
          const identity = ensureTempViewerIdentity();
          if (!identity) return;
          await postComment(currentSlide.slideId, commentContent, identity);
        } else {
          await postComment(currentSlide.slideId, commentContent);
        }
      } else {
        // 수정: 비밀번호는 editComment 비번 모달에서 받아 commentEditPassword에 저장됨
        const anonymous = commentEditPassword ? { anonymousPassword: commentEditPassword } : undefined;
        const result = await putComment(currentCommentId, commentContent, anonymous);
        if (!result) {
          message.error("비밀번호가 올바르지 않거나 수정에 실패했습니다.");
          return;
        }
        message.success("코멘트가 수정되었습니다.");
      }
      await refreshSlide();
    }
    setShowCommentModal(false);
    setCommentContent("");
    setCommentEditPassword("");
    setCurrentCommentId(0);
  };

  // 코멘트 수정 요청 → 바로 수정 모달 오픈 (비밀번호는 수정 모달에서 입력)
  const clickEditComment = (comment: any) => {
    if (isConsultationMode && comment.anonymous === true) {
      if (storedName !== comment.writer) {
        window.alert("수정 권한이 없습니다.");
        return;
      }
    }
    setCommentContent(comment.content);
    setCurrentCommentId(comment.slideCommentId);
    setCommentEditPassword("");
    setShowCommentModal(true);
  };

  // 코멘트 삭제 요청 → 비밀번호 모달 오픈
  const clickDeleteComment = (comment: any) => {
    if (isConsultationMode && comment.anonymous === true) {
      if (storedName !== comment.writer) {
        window.alert("삭제 권한이 없습니다.");
        return;
      }
      setPendingAnnoAction({ type: "deleteComment", comment });
      setAnnoPasswordInput("");
      setAnnoPasswordModalOpen(true);
    } else {
      if (window.confirm("해당 코멘트를 삭제하시겠습니까?")) {
        deleteComment(comment.slideCommentId).then(() => refreshSlide());
      }
    }
  };

  // 어노테이션 수정 요청 → 바로 수정 모달 오픈 (비밀번호는 수정 모달에서 입력)
  const handleEditAnnotationFromSidebar = (annotation: any) => {
    const latestAnnotation = currentSlide?.annotations?.find(
      (anno: any) => anno.annoId === annotation.annoId
    );
    if (!latestAnnotation) return;

    if (storedName !== latestAnnotation.writer) {
      window.alert("수정 권한이 없습니다.");
      return;
    }

    annotationInfo.setSelectedAnnotation(latestAnnotation);
    annotationInfo.annotationForm.setFieldsValue({
      content: latestAnnotation.content,
      anonymousPassword: "",
    });
    annotationInfo.setShowEditModal(true);
  };

  // 어노테이션 삭제 요청 → 작성자 확인 후 비밀번호 모달 오픈
  const handleDeleteAnnotationFromSidebar = (annotation: any) => {
    const latestAnnotation = currentSlide?.annotations?.find(
      (anno: any) => anno.annoId === annotation.annoId
    );
    if (!latestAnnotation) return;

    if (storedName !== latestAnnotation.writer) {
      window.alert("삭제 권한이 없습니다.");
      return;
    }

    setPendingAnnoAction({ type: "delete", annotation: latestAnnotation });
    setAnnoPasswordInput("");
    setAnnoPasswordModalOpen(true);
  };

  // 비밀번호 모달 확인
  const handleAnnoPasswordConfirm = async () => {
    if (!pendingAnnoAction) return;
    if (!annoPasswordInput.trim()) {
      message.warning("비밀번호를 입력해주세요.");
      return;
    }

    if (pendingAnnoAction.type === "delete") {
      const result = await deleteAnnotation(pendingAnnoAction.annotation.annoId, annoPasswordInput);
      if (!result) {
        message.error("비밀번호가 올바르지 않거나 삭제에 실패했습니다.");
        return;
      }
      message.success("어노테이션이 삭제되었습니다.");
      setAnnoPasswordModalOpen(false);
      setPendingAnnoAction(null);
      setAnnoPasswordInput("");
      await refreshSlide();
    } else if (pendingAnnoAction.type === "deleteComment") {
      const result = await deleteComment(pendingAnnoAction.comment.slideCommentId, annoPasswordInput);
      if (!result) {
        message.error("비밀번호가 올바르지 않거나 삭제에 실패했습니다.");
        return;
      }
      message.success("코멘트가 삭제되었습니다.");
      setAnnoPasswordModalOpen(false);
      setPendingAnnoAction(null);
      setAnnoPasswordInput("");
      await refreshSlide();
    }
  };

  useEffect(() => {}, [currentSlide]);

  const DisplayColor = (props: any) => {
    const { annotation } = props;
    const annoInfo = annotation?.annoInfo;
    let color;
    if (annoInfo && annoInfo[0] === 'g' && annoInfo[1]?.class === 'annotation arrow-group') {
      color = annoInfo[1]?.stroke || '#285F3D';
    } else {
      color = annoInfo?.[1]?.stroke;
    }
    return (
      <div style={{ width: "1rem", height: "1rem", backgroundColor: color, borderRadius: "3px", marginLeft: "3px" }} />
    );
  };

  const renderContent = () => {
    switch (activePanel) {
      case 'info':
        return (
          <>
            <PanelHeader>
              <PanelTitle>INFORMATION</PanelTitle>
              <CloseButton onClick={onClose}><CloseOutlined /></CloseButton>
            </PanelHeader>
            <PanelContent>
              <InfoGrid>
                <StInfoLabel>Slide Name</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.slidename : "-"}</StInfoValue>
                <StInfoLabel>Caption</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.caption : "-"}</StInfoValue>
                <StInfoLabel>Gender</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.gender : "-"}</StInfoValue>
                <StInfoLabel>Age</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.age : "-"}</StInfoValue>
                <StInfoLabel>Site</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.site : "-"}</StInfoValue>
                <StInfoLabel>Diagnosis</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.zindanName : "-"}</StInfoValue>
              </InfoGrid>
            </PanelContent>
          </>
        );

      case 'annotation':
        return (
          <>
            <PanelHeader>
              <PanelTitle>ANNOTATIONS</PanelTitle>
              <CloseButton onClick={onClose}><CloseOutlined /></CloseButton>
            </PanelHeader>
            <PanelContent>
              {(currentSlide?.annotations || []).map((annotation, idx) => (
                <StAnnotationBox key={`${idx}_${annotation.annoId}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.8rem" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.3rem" }}>
                        <DisplayColor annotation={annotation} />
                        <div
                          title={annotation.content}
                          style={{ fontSize: "1rem", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "10rem", color: "#fff" }}
                        >
                          {annotation.content}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#1890ff", padding: "2px 6px", backgroundColor: "#051e34", border: "1px solid #1e3a52", borderRadius: "3px", flexShrink: 0 }}>
                          {annotationInfo.getShapeType(annotation)}
                        </div>
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "#8899aa", marginLeft: "1.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        {annotation.writer}
                        {annotation.anonymous === true && (
                          <StTempUserLabel>(링크 접근 사용자)</StTempUserLabel>
                        )}
                      </div>
                    </div>
                    {isConsultationMode && annotation.anonymous === true && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                        <EditOutlined
                          onClick={() => handleEditAnnotationFromSidebar(annotation)}
                          style={{ fontSize: "1.1rem", cursor: "pointer", color: "#8899aa" }}
                        />
                        <DeleteOutlined
                          onClick={() => handleDeleteAnnotationFromSidebar(annotation)}
                          style={{ fontSize: "1.1rem", cursor: "pointer", color: "#8899aa" }}
                        />
                      </div>
                    )}
                  </div>
                </StAnnotationBox>
              ))}
            </PanelContent>
          </>
        );

      case 'comment':
        return (
          <>
            <PanelHeader>
              <PanelTitle>COMMENTS</PanelTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#8899aa' }}>
                {isConsultationMode && (
                  <PlusCircleOutlined
                    onClick={() => { setCurrentCommentId(0); setCommentContent(""); setShowCommentModal(true); }}
                    style={{ fontSize: "1.2rem", cursor: "pointer" }}
                  />
                )}
                <CloseButton onClick={onClose}><CloseOutlined /></CloseButton>
              </div>
            </PanelHeader>
            <PanelContent>
              {(currentSlide?.slideComments || []).map((comment) => (
                <StCommentBox key={comment.slideCommentId}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                    <div style={{ fontSize: "0.95rem", color: "#fff", lineHeight: "1.5", wordBreak: "break-word", flex: 1, marginBottom: "0.4rem" }}>
                      {comment.content}
                    </div>
                    {isConsultationMode && comment.anonymous === true && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                        <EditOutlined
                          onClick={() => clickEditComment(comment)}
                          style={{ fontSize: "1rem", cursor: "pointer", color: "#8899aa" }}
                        />
                        <DeleteOutlined
                          onClick={() => clickDeleteComment(comment)}
                          style={{ fontSize: "1rem", cursor: "pointer", color: "#8899aa" }}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#8899aa", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    {comment.writer}
                    {comment.anonymous === true && (
                      <StTempUserLabel>(링크 접근 사용자)</StTempUserLabel>
                    )}
                  </div>
                </StCommentBox>
              ))}
            </PanelContent>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <StMainWrapper>
      {renderContent()}

      {/* 코멘트 추가/수정 모달 */}
      <Modal
        centered
        title={currentCommentId === 0 ? "Add Comment" : "Edit Comment"}
        open={showCommentModal}
        onOk={addComment}
        onCancel={() => { setShowCommentModal(false); setCommentContent(""); setCommentEditPassword(""); setCurrentCommentId(0); }}
      >
        <Input
          value={commentContent}
          onChange={(e) => setCommentContent(e.target.value)}
          onPressEnter={addComment}
          placeholder="Please input your comment"
          style={{ marginBottom: currentCommentId !== 0 && commentEditPassword !== undefined ? '8px' : 0 }}
        />
        {currentCommentId !== 0 && (
          <Input.Password
            value={commentEditPassword}
            onChange={(e) => setCommentEditPassword(e.target.value)}
            placeholder="등록 시 사용한 비밀번호를 입력하세요"
          />
        )}
      </Modal>

      {/* 어노테이션 수정/삭제 비밀번호 확인 모달 */}
      <Modal
        centered
        title="비밀번호 확인"
        open={annoPasswordModalOpen}
        onOk={handleAnnoPasswordConfirm}
        onCancel={() => { setAnnoPasswordModalOpen(false); setPendingAnnoAction(null); setAnnoPasswordInput(""); }}
        okText="확인"
        cancelText="취소"
      >
        <Input.Password
          placeholder="등록 시 사용한 4자리 비밀번호를 입력하세요"
          value={annoPasswordInput}
          onChange={(e) => setAnnoPasswordInput(e.target.value)}
          onPressEnter={handleAnnoPasswordConfirm}
          style={{ marginTop: "8px" }}
        />
      </Modal>

      {isConsultationMode && <AnnotationInfoPanelComponent {...annotationInfo} />}
    </StMainWrapper>
  );
};

const StMainWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StAnnotationBox = styled.div`
  padding: 0.4rem;
  border: 1px solid #1e3a52;
  margin-bottom: 0.5rem;
  background-color: #051e34;
  border-radius: 4px;
  &:last-child { margin-bottom: 0; }
`;

const StCommentBox = styled.div`
  padding: 0.8rem;
  background-color: #051e34;
  border-radius: 8px;
  margin-bottom: 0.6rem;
  border-left: 3px solid #1890ff;
  &:last-child { margin-bottom: 0; }
`;

const StInfoLabel = styled.div`
  border-right: 1px solid #1e3a52;
  font-size: 1rem;
  font-weight: 600;
  color: #8899aa;
`;

const StInfoValue = styled.div`
  margin-left: 1rem;
  color: #fff;
`;

const StTempUserLabel = styled.span`
  font-size: 0.6875rem;
  color: #8899aa;
  font-weight: normal;
`;

const PanelHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #1e3a52;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #051e34;
  flex-shrink: 0;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: #8899aa;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  &:hover { color: #fff; }
`;

const PanelContent = styled.div`
  padding: 15px;
  overflow-y: auto;
  flex: 1;
  background-color: #0a1929;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #051e34; }
  &::-webkit-scrollbar-thumb { background: #1e3a52; border-radius: 3px; &:hover { background: #2d5171; } }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 8rem auto;
  row-gap: 10px;
`;

export default DemoSideMenu;
